/**
 * View-models. Pure functions from (content, run) to plain data the app
 * renders and the tests assert on. Nothing here mutates the run.
 */

import { evaluate } from './conditions';
import type { ContentIndex } from './content';
import { optionAvailability, type Run } from './engine';
import { describeFollowOn, trustLabel, type FollowOnView } from './followon';
import type { Character, Condition, DecisionNode, Evidence, Line, LogEntry, Node, Option, Phase, RunState } from './types';
import { deriveEncounters, unlockMet, type Encounters } from './unlocks';

export interface SpeakerView {
  id: string;
  display: string;
  name: string;
  role: string;
  portrait: string | null;
  kind: Character['kind'];
}

export interface EvidenceLink {
  id: string;
  title: string;
  acquired: boolean;
}

export interface LineView {
  speaker: SpeakerView | null;
  text: string;
  cites: EvidenceLink[];
}

export interface OptionView {
  id: string;
  intent: string;
  statement: string | null;
  subtitle: string | null;
  evidence: EvidenceLink[];
  attraction: string | null;
  cost: string;
  uncertainty: string;
  available: boolean;
  reason: string | null;
  chosen: boolean;
  supported_by: { fact: string; label: string; ready: boolean }[];
}

export interface NodeView {
  phase: { id: string; title: string; display_time: string; contact: Phase['contact']; alternate_history: boolean; index: number; count: number };
  attention: { remaining: number; declared: number } | null;
  node: { id: string; type: Node['type']; title: string | null; header_label: string | null; text: string | null; prompt: string | null };
  lines: LineView[];
  questions: { id: string; text: string; asked: boolean; answer: LineView }[];
  readout: { label: string; ready: boolean; text: string }[] | null;
  options: OptionView[] | null;
  continue: { id: string; label: string } | null;
  status: string[] | null;
  event_text: string | null;
  resolution: string | null;
  acquired_here: string[];
  /** Effects the current event node applied on arrival, in order, as player-readable labels. */
  applied: { kind: 'fact' | 'trust' | 'procedure' | 'note'; label: string }[];
}

export interface EvidenceView {
  id: string;
  kind: Evidence['kind'];
  title: string;
  body: string | null;
  eligible: boolean;
  simulated: boolean;
  badge: 'REFERENCE' | 'CURRENT CONTACT' | 'PREVIOUS CONTACT';
  stage: string;
  channel: string;
  observation: 'known' | 'unknown' | null;
  provenance: Evidence['provenance'];
  at_event: number;
}

function speaker(content: ContentIndex, id: string | null): SpeakerView | null {
  if (!id) return null;
  const c = content.characters.get(id);
  if (!c) return { id, display: id, name: id, role: '', portrait: null, kind: 'composite-controller' };
  return { id: c.id, display: c.display, name: c.name, role: c.role, portrait: c.portrait, kind: c.kind };
}

function link(content: ContentIndex, state: RunState, id: string): EvidenceLink {
  const e = content.evidence.get(id);
  return { id, title: e?.title ?? id, acquired: Object.prototype.hasOwnProperty.call(state.mission.evidence, id) };
}

/** Citation links name only items the player can already see (R2): an acquired-but-locked item is absent, not greyed. */
function lineView(content: ContentIndex, state: RunState, l: Line, unlocked?: Set<string>): LineView {
  const cites = (l.cites ?? []).filter((c) => !unlocked || unlocked.has(c));
  return { speaker: speaker(content, l.speaker), text: l.text, cites: cites.map((c) => link(content, state, c)) };
}

// ---------------------------------------------------------------------------
// Encounter-based availability (content 0.5.5, R2): what the Binder, the Evidence list, the citation links and the
// History panel may show is acquisition / adoption AND the encounter that presents the item, derived from the inputs.
// ---------------------------------------------------------------------------

/** Evidence the player can see: acquired, its unlock met, and eligible under any visible_when. Newest first. */
export function describeVisibleEvidence(run: Run): EvidenceView[] {
  const enc = deriveEncounters(run);
  return describeEvidence(run.content, run.state).filter((e) => e.eligible && unlockMet(run.content.evidence.get(e.id)?.unlocked_by, enc));
}

/** The ids of the evidence the player can see. */
export function unlockedEvidenceIds(run: Run): Set<string> {
  return new Set(describeVisibleEvidence(run).map((e) => e.id));
}

/** Procedures in the Binder: adopted in the ledger AND their unlock met; the ledger's order. */
export function visibleProcedures(run: Run): string[] {
  const enc = deriveEncounters(run);
  return run.state.ledger.procedures.filter((id) => unlockMet(run.content.procedures.get(id)?.unlocked_by, enc));
}

export interface HistoryVisibility {
  /** The alternate-history explanation names later branches: shown once play has left the record, or after the mission. */
  explanation: boolean;
  /** The prologue's facility note: shown once the plates were walked (Skip does not count), whatever the run. */
  prologueNote: boolean;
  /** The CAPCOM note: shown once a historical person has spoken a displayed line. */
  capcomNote: boolean;
  /** Each registry source: its title once something displayed cites it (every source after the mission); its note after the mission, or at once for a source only the prologue cites. */
  sources: { id: string; title: boolean; note: boolean }[];
}

/** What the History panel may show now (R2). `prologueSeen` is presentation state the app keeps; `run` may be null before a campaign. */
export function describeHistory(content: ContentIndex, run: Run | null, prologueSeen: boolean): HistoryVisibility {
  const enc: Encounters | null = run ? deriveEncounters(run) : null;
  const completed = !!run?.state.mission.completed;
  const prologue = content.mission.prologue;
  const prologueSources = new Set<string>([...(prologue?.history_sources ?? []), ...(prologue?.plates ?? []).flatMap((p) => p.sources ?? [])]);
  // Sources cited by play material (evidence, lines, the debrief) — anything else is prologue-only or uncited.
  const playCiters = new Set<string>();
  for (const e of content.evidence.values()) for (const s of e.provenance.sources) playCiters.add(s);
  for (const { node } of content.nodes.values()) {
    const lines: Line[] = node.type === 'briefing' || node.type === 'decision' ? [...(node.lines ?? []), ...(node.questions ?? []).map((q) => q.answer)] : node.type === 'event' ? node.resolutions.flatMap((r) => r.lines ?? []) : [];
    for (const l of lines) for (const s of l.provenance?.sources ?? []) playCiters.add(s);
  }
  for (const d of content.mission.debrief) for (const s of d.provenance?.sources ?? []) playCiters.add(s);
  const cited = new Set<string>();
  if (run && enc) {
    for (const e of describeVisibleEvidence(run)) for (const s of e.provenance.sources) cited.add(s);
    for (const s of enc.sources) cited.add(s);
    if (completed) for (const d of content.mission.debrief) if (evaluate(d.when, run.state)) for (const s of d.provenance?.sources ?? []) cited.add(s);
  }
  if (prologueSeen) for (const s of prologueSources) cited.add(s);
  const capcomNote = !!enc && [...enc.speakers].some((id) => content.characters.get(id)?.kind === 'historical');
  return {
    explanation: !!run && (completed || alternateHistoryActive(run)),
    prologueNote: prologueSeen,
    capcomNote,
    sources: content.bundle.registry.sources.map((s) => ({
      id: s.id,
      title: completed || cited.has(s.id),
      note: completed || (prologueSeen && prologueSources.has(s.id) && !playCiters.has(s.id)),
    })),
  };
}

/**
 * Sticky presentation marker, derived without adding save/domain state.
 * Project only condition inputs from the existing log, testing completed input
 * boundaries (and the current boundary). This preserves activation even for
 * negated conditions that become false later, including after save/replay.
 * Only phases actually entered can activate a marker.
 */
export function alternateHistoryActive(run: Run): boolean {
  const state: RunState = {
    ...run.state,
    ledger: { ...run.state.ledger, facts: { ...run.identity.initial_ledger.facts }, procedures: [...run.identity.initial_ledger.procedures] },
    mission: { ...run.state.mission, evidence: {}, chosen: [], events_seen: [] },
  };
  const markers: (boolean | Condition)[] = [];
  const active = () => markers.some((m) => m === true || (typeof m === 'object' && evaluate(m, state)));
  for (const entry of run.log) {
    if (entry.type === 'input' && active()) return true;
    if (entry.type === 'phase') {
      const marker = run.content.mission.phases.find((p) => p.id === entry.phase)?.alternate_history;
      if (marker !== undefined) markers.push(marker);
    } else if (entry.type === 'input' && entry.input.kind === 'option') {
      state.mission.chosen.push({ option: entry.input.option, node: entry.input.node, at_event: entry.seq });
    } else if (entry.type === 'acquire') {
      state.mission.evidence[entry.evidence] = { at_event: entry.seq, phase: entry.phase, stage: entry.stage, channel: entry.channel, node: entry.node };
    } else if (entry.type === 'resolution') {
      state.mission.events_seen.push(entry.node, entry.resolution);
    } else if (entry.type === 'effect' && entry.effect === 'set_fact') {
      state.ledger.facts[entry.fact] = { set_by: state.mission.id, at_event: entry.seq, label: entry.label };
    } else if (entry.type === 'effect' && entry.effect === 'adopt_procedure') {
      state.ledger.procedures.push(entry.procedure);
    }
  }
  return active();
}

function optionView(content: ContentIndex, state: RunState, node: Node, o: Option, unlocked?: Set<string>): OptionView {
  const avail = optionAvailability(state, node, o);
  return {
    id: o.id,
    intent: o.intent,
    statement: o.statement ?? null,
    subtitle: o.subtitle ?? null,
    evidence: o.evidence.filter((e) => !unlocked || unlocked.has(e)).map((e) => link(content, state, e)),
    attraction: o.attraction ?? null,
    cost: o.cost,
    uncertainty: o.uncertainty,
    available: avail.available,
    reason: avail.reason ?? null,
    chosen: state.mission.chosen.some((c) => c.option === o.id),
    supported_by: (o.supported_by ?? []).map((f) => ({ fact: f, label: factLabel(content, f), ready: Object.prototype.hasOwnProperty.call(state.ledger.facts, f) })),
  };
}

function factLabel(content: ContentIndex, fact: string): string {
  // Look for an authored label on any set_fact effect; fall back to the humanized id.
  for (const { option } of content.options.values()) {
    for (const e of option.effects) if ('set_fact' in e && e.set_fact === fact && e.label) return e.label;
  }
  for (const { resolution } of content.resolutions.values()) {
    for (const e of resolution.effects) if ('set_fact' in e && e.set_fact === fact && e.label) return e.label;
  }
  return fact.replace(/^g\d+[a-z]?-/, '').replace(/-/g, ' ');
}


/**
 * The decision the player has just committed in this phase (the node before
 * the current briefing), with its options as the player saw them, so the
 * receipt screen can keep the cards on screen stamped and greyed. Null when
 * the current node is not a briefing that directly follows a chosen decision
 * in the same phase. Read-only; nothing here mutates the run.
 */
export function describeCommittedDecision(run: Run): { node: DecisionNode; options: OptionView[] } | null {
  const cur = run.currentNode();
  if (!cur || cur.node.type !== 'briefing') return null;
  const ref = run.content.nodes.get(cur.node.id);
  if (!ref || ref.nodeIndex === 0) return null;
  const prev = cur.phase.nodes[ref.nodeIndex - 1];
  if (!prev || prev.type !== 'decision') return null;
  if (!run.state.mission.chosen.some((c) => c.node === prev.id)) return null;
  const unlocked = unlockedEvidenceIds(run);
  return { node: prev, options: prev.options.map((o) => optionView(run.content, run.state, prev, o, unlocked)) };
}

export function describeNode(run: Run): NodeView | null {
  const content = run.content;
  const state = run.state;
  const cur = run.currentNode();
  if (!cur) return null;
  const { phase, node } = cur;
  const phaseIndex = state.mission.cursor!.phase;
  const unlocked = unlockedEvidenceIds(run);

  const lines: Line[] = node.type === 'briefing' || node.type === 'decision' ? node.lines ?? [] : [];
  const visibleLines = lines.filter((l) => !l.when || evaluate(l.when, state)).map((l) => lineView(content, state, l, unlocked));

  let text: string | null = null;
  let status: string[] | null = null;
  let eventText: string | null = null;
  let resolution: string | null = null;
  if (node.type === 'briefing') {
    const variant = node.variants?.find((v) => evaluate(v.when, state));
    text = variant?.text ?? node.text ?? null;
  } else if (node.type === 'decision' || node.type === 'prep_choice') {
    text = node.text ?? null;
  } else {
    const rid = state.mission.node_resolution[node.id] ?? null;
    resolution = rid;
    const r = rid ? content.resolutions.get(rid)?.resolution : undefined;
    if (r) {
      status = r.status ?? null;
      eventText = r.text ?? null;
      for (const l of r.lines ?? []) visibleLines.push(lineView(content, state, l, unlocked));
    }
  }

  const questions = (node.type === 'briefing' || node.type === 'decision' ? node.questions ?? [] : []).map((q) => ({
    id: q.id,
    text: q.text,
    asked: state.mission.questions_asked.includes(q.id),
    answer: lineView(content, state, q.answer, unlocked),
  }));

  const readout = node.type === 'decision' && node.readout
    ? node.readout.map((r) => {
      const ready = Object.prototype.hasOwnProperty.call(state.ledger.facts, r.fact);
      return { label: r.label, ready, text: ready ? r.ready : r.not_ready };
    })
    : null;

  const options = node.type === 'prep_choice' || node.type === 'decision' ? node.options.map((o) => optionView(content, state, node, o, unlocked)) : null;
  const cont = node.type === 'prep_choice' ? node.finish : node.type === 'decision' ? null : node.continue;

  const acquiredHere = Object.entries(state.mission.evidence).filter(([, rec]) => rec.node === node.id).map(([id]) => id);
  const applied: NodeView['applied'] = [];
  if (resolution) {
    for (const e of run.log) {
      if (e.type !== 'effect' || e.cause !== resolution) continue;
      if (e.effect === 'set_fact') applied.push({ kind: 'fact', label: e.label });
      else if (e.effect === 'adjust') {
        const who = content.characters.get(e.path.split('.')[1] ?? '')?.display ?? e.path;
        applied.push({ kind: 'trust', label: `${who}: trust ${e.before} → ${e.after} (${e.by >= 0 ? '+' : ''}${e.by})` });
      } else if (e.effect === 'adopt_procedure') applied.push({ kind: 'procedure', label: `Binder: ${content.procedures.get(e.procedure)?.title ?? e.procedure}` });
      else if (e.effect === 'append_note') applied.push({ kind: 'note', label: `${content.characters.get(e.person)?.display ?? e.person} — note: ${factLabel(content, e.fact)}` });
    }
  }

  return {
    phase: {
      id: phase.id, title: phase.title, display_time: phase.display_time, contact: phase.contact,
      alternate_history: alternateHistoryActive(run), index: phaseIndex, count: content.mission.phases.length,
    },
    attention: phase.attention === null || state.mission.attention === null ? null : { remaining: state.mission.attention, declared: phase.attention },
    node: {
      id: node.id, type: node.type,
      title: node.type === 'event' || node.type === 'briefing' || node.type === 'decision' ? node.title ?? null : null,
      header_label: node.type === 'briefing' || node.type === 'decision' ? node.header_label ?? null : node.type === 'event' ? node.label ?? null : null,
      text,
      prompt: node.type === 'prep_choice' || node.type === 'decision' ? node.prompt : null,
    },
    lines: visibleLines,
    questions,
    readout,
    options,
    continue: cont ? { id: cont.id, label: cont.label } : null,
    status,
    event_text: eventText,
    resolution,
    acquired_here: acquiredHere,
    applied,
  };
}

export function describeEvidence(content: ContentIndex, state: RunState): EvidenceView[] {
  const curPhase = state.mission.cursor ? content.mission.phases[state.mission.cursor.phase]?.id ?? null : null;
  const out: EvidenceView[] = [];
  for (const [id, rec] of Object.entries(state.mission.evidence)) {
    const e = content.evidence.get(id);
    if (!e) continue;
    const eligible = !e.visible_when || evaluate(e.visible_when, state);
    const badge: EvidenceView['badge'] = e.kind === 'reference' ? 'REFERENCE' : rec.phase === curPhase ? 'CURRENT CONTACT' : 'PREVIOUS CONTACT';
    out.push({
      id, kind: e.kind, title: e.title, body: eligible ? e.body : null, eligible, simulated: e.simulated, badge,
      stage: rec.stage, channel: rec.channel, observation: e.observation ?? null, provenance: e.provenance, at_event: rec.at_event,
    });
  }
  out.sort((a, b) => b.at_event - a.at_event);
  return out;
}

export interface Adjustment {
  person: string;
  display: string;
  before: number;
  after: number;
  by: number;
  label: string;
}

export interface DebriefView {
  outcome: { id: string; title: string; kind: string };
  consequence: { resolution: string; text: string } | null;
  relationship: { resolution: string; lines: LineView[]; adjustments: Adjustment[] } | null;
  controllers: { id: string; display: string; before: number; after: number; label: string; notes: string[] }[];
  constraint: { fact: string; text: string } | null;
  postflight: {
    stance: string | null;
    statement: string | null;
    response: { resolution: string; text: string } | null;
    astronauts: { id: string; display: string; before: number; after: number; label: string; notes: string[] }[];
    status: { id: string; title: string; text: string; procedure: string | null }[];
    context: EvidenceView | null;
  };
  procedures: { id: string; title: string; text: string; status: string }[];
  paragraphs: { id: string; text: string; section?: 'departures'; provenance?: { sources: string[]; fiction: string[]; note: string } }[];
  events: LogEntry[];
}

function adjustmentsFor(content: ContentIndex, log: readonly LogEntry[], cause: string): Adjustment[] {
  const out: Adjustment[] = [];
  for (const entry of log) {
    if (entry.type === 'effect' && entry.effect === 'adjust' && entry.cause === cause) {
      const person = entry.path.split('.')[1] ?? '';
      const c = content.characters.get(person);
      out.push({ person, display: c?.display ?? person, before: entry.before, after: entry.after, by: entry.by, label: trustLabel(content.followon, entry.after) });
    }
  }
  return out;
}

export function describeDebrief(run: Run): DebriefView | null {
  const content = run.content;
  const state = run.state;
  const done = state.mission.completed;
  if (!done) return null;
  const layout = content.mission.debrief_layout;
  const log = run.log;

  const consRes = state.mission.node_resolution[layout.consequence_node];
  const consequence = consRes ? { resolution: consRes, text: content.resolutions.get(consRes)?.resolution.text ?? '' } : null;

  const relRes = state.mission.node_resolution[layout.relationship_node];
  const relationship = relRes
    ? {
      resolution: relRes,
      lines: (content.resolutions.get(relRes)?.resolution.lines ?? []).map((l) => lineView(content, state, l)),
      adjustments: adjustmentsFor(content, log, relRes),
    }
    : null;

  const personRow = (id: string) => {
    const p = state.ledger.people[id] ?? { trust: 0, notes: [] };
    const first = log.find((e) => e.type === 'effect' && e.effect === 'adjust' && e.path === `people.${id}.trust`);
    const before = first && first.type === 'effect' && first.effect === 'adjust' ? first.before : p.trust;
    const c = content.characters.get(id);
    return { id, display: c?.display ?? id, before, after: p.trust, label: trustLabel(content.followon, p.trust), notes: p.notes.map((id) => state.ledger.facts[id]?.label ?? 'No description recorded') };
  };

  const constraintFact = layout.constraint_facts.find((f) => Object.prototype.hasOwnProperty.call(state.ledger.facts, f)) ?? null;
  const constraintText = constraintFact ? content.followon.constraint.find((c) => evaluate(c.when, state))?.text ?? null : null;

  const accRes = state.mission.node_resolution[layout.accountability_node];
  const stanceChoice = state.mission.chosen.find((c) => content.nodes.get(c.node)?.node.type === 'decision' && content.options.get(c.option)?.option.statement);
  const stanceOption = stanceChoice ? content.options.get(stanceChoice.option)?.option : undefined;
  const fo = describeFollowOn(content, state.ledger, done, state.followon.committed);
  const contextEv = describeVisibleEvidence(run).find((e) => e.id === 'g8-ev-postflight-context') ?? null;

  const paragraphs = content.mission.debrief.filter((r) => evaluate(r.when, state)).map((r) => ({ id: r.id, text: r.text, section: r.section, provenance: r.provenance }));

  return {
    outcome: { id: done.outcome, title: done.title, kind: done.kind },
    consequence,
    relationship,
    controllers: layout.controllers.map(personRow),
    constraint: constraintFact ? { fact: constraintFact, text: constraintText ?? '' } : null,
    postflight: {
      stance: stanceOption?.id ?? null,
      statement: stanceOption?.statement ?? null,
      response: accRes ? { resolution: accRes, text: content.resolutions.get(accRes)?.resolution.text ?? '' } : null,
      astronauts: layout.astronauts.map(personRow),
      status: fo.status_blocks,
      context: contextEv,
    },
    procedures: state.ledger.procedures.map((id) => {
      const p = content.procedures.get(id);
      return { id, title: p?.title ?? id, text: p?.text ?? '', status: p?.status ?? '' };
    }),
    paragraphs,
    events: log.filter((e) => e.player_visible),
  };
}

export function describeFollowOnForRun(run: Run): FollowOnView | null {
  const done = run.state.mission.completed;
  if (!done || !run.state.followon.active) return null;
  return describeFollowOn(run.content, run.state.ledger, done, run.state.followon.committed);
}

export function conditionHolds(run: Run, cond: Condition): boolean {
  return evaluate(cond, run.state);
}
